import React, { useState, useEffect } from "react";
import { makeRequest2 } from '../../../utils/makeRequest';
import { toastify } from '../../../utils/toast';
import { getItem } from "../../../utils/localStorage";
import { ADD_CURRENCY_URL } from '../../../utils/urls';
import { currenciesData } from "../../../utils/data";

const CurrencyTab = ({ onComplete, isCompleted, existingData }) => {
    const [loading, setLoading] = useState(false);
    const [currencyName, setCurrencyName] = useState("");
    const [currencyShortCode, setCurrencyShortCode] = useState("");
    const [exchangeRate, setExchangeRate] = useState("");
    const [isDefaultCurrency, setIsDefaultCurrency] = useState(false);
    const [selectedCurrency, setSelectedCurrency] = useState(null);

    // Generate currency options from currenciesData
    const currencyOptions = currenciesData?.map(currency => ({
        value: currency.code,
        label: `${currency.name} (${currency.code})`
    })) || [];

    // 🔹 Prefill with existingData if available
    useEffect(() => {
        if (existingData) {
            setCurrencyName(existingData.currencyName || "");
            setCurrencyShortCode(existingData.currencyShortCode || "");
            setExchangeRate(existingData.exchangeRate || "");
            setIsDefaultCurrency(existingData.isDefaultCurrency || false);

            // match the dropdown option
            const matchedOption = currencyOptions.find(opt => opt.value === existingData.currencyShortCode);
            if (matchedOption) {
                setSelectedCurrency(matchedOption);
            }
        }
    }, [existingData, currencyOptions]);

    // Handle currency selection
    const handleCurrencySelect = (option) => {
        setSelectedCurrency(option);
        if (option) {
            setCurrencyName(option.label.split(' - ')[1]?.split(' (')[0] || option.label);
            setCurrencyShortCode(option.value);
        } else {
            setCurrencyName('');
            setCurrencyShortCode('');
        }
    };

    const validateCurrency = () => {
        return currencyName &&
            currencyShortCode &&
            (exchangeRate || isDefaultCurrency);
    };

    const handleCurrencySubmit = async () => {
        if (!currencyName || !currencyShortCode) {
            toastify("Please select a currency", "error");
            return;
        }

        if (!exchangeRate && !isDefaultCurrency) {
            toastify("Please enter an exchange rate", "error");
            return;
        }

        setLoading(true);

        try {
            const facilityId = await getItem("selectedFacilityId");
            const data = {
                currencyName,
                currencyShortCode,
                exchangeRate: isDefaultCurrency ? 1 : parseFloat(exchangeRate),
                isDefaultCurrency
            };

            const response = await makeRequest2(
                `${ADD_CURRENCY_URL}/${facilityId}`,
                "POST",
                data
            );

            if (response?.success === true) {
                toastify("Currency added successfully", "success");
                onComplete(); // Mark this step as completed
            } else {
                const errorMessage = response?.message || response?.error || "Operation failed";
                toastify(errorMessage, "error");
            }
        } catch (error) {
            console.error("Submit error:", error);
            toastify(error.message || "Error submitting currency", "error");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="tab-pane fade show active">
            <h4 className="mb-4">Select Your Currency</h4>
            {isCompleted && !existingData && (
                <div className="alert alert-info mt-3">
                    <i className="bi bi-check-circle-fill me-2"></i>
                    Currency has been saved successfully!
                </div>
            )}
            <div>
                <div className="mb-3">
                    <label htmlFor="currencySelect" className="form-label">Search and Select Country *</label>
                    <select
                        className="form-select form-select-lg"
                        id="currencySelect"
                        value={selectedCurrency?.value || ''}
                        onChange={(e) => {
                            const option = currencyOptions.find(opt => opt.value === e.target.value);
                            handleCurrencySelect(option);
                        }}
                    >
                        <option value="">Search a country...</option>
                        {currencyOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="row">
                    <div className="col-md-6 mb-3">
                        <label htmlFor="currencyName" className="form-label">Currency Name *</label>
                        <input
                            type="text"
                            className="form-control"
                            id="currencyName"
                            value={currencyName}
                            onChange={(e) => setCurrencyName(e.target.value)}
                            placeholder="Currency name will auto-fill"
                            disabled
                        />
                    </div>
                    <div className="col-md-6 mb-3">
                        <label htmlFor="currencyCode" className="form-label">Currency Code *</label>
                        <input
                            type="text"
                            className="form-control"
                            id="currencyCode"
                            value={currencyShortCode}
                            onChange={(e) => setCurrencyShortCode(e.target.value)}
                            placeholder="Currency code will auto-fill"
                            disabled
                        />
                    </div>
                </div>

                <div className="mb-3">
                    <label htmlFor="exchangeRate" className="form-label">Exchange Rate *</label>
                    <input
                        type="number"
                        className="form-control"
                        id="exchangeRate"
                        min="0.001"
                        step="0.001"
                        value={exchangeRate}
                        onChange={(e) => setExchangeRate(e.target.value)}
                        placeholder="Enter exchange rate"
                        disabled={isDefaultCurrency}
                    />
                    <small className="text-muted">
                        Rate relative to the default currency
                    </small>
                </div>

                <div className="form-check mb-4">
                    <input
                        type="checkbox"
                        className="form-check-input"
                        id="defaultCurrency"
                        checked={isDefaultCurrency}
                        onChange={(e) => setIsDefaultCurrency(e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="defaultCurrency">
                        Set as default currency
                    </label>
                </div>

                {currencyName && (
                    <div className="alert alert-info">
                        <i className="bi bi-info-circle me-2"></i>
                        You've selected <strong>{currencyName} ({currencyShortCode})</strong> as your currency.
                        {isDefaultCurrency && ' This will be set as your default currency.'}
                    </div>
                )}

                <div className="text-end">
                    <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleCurrencySubmit}
                        disabled={loading || !validateCurrency()}
                    >
                        {loading ? 'Saving...' : 'Save Currency'}
                    </button>
                </div>

                <small className="text-muted">* Required fields</small>
            </div>
            {isCompleted && existingData && (
                <div className="alert alert-success mt-3">
                    <i className="bi bi-check-circle-fill me-2"></i>
                    Currency has been saved successfully!
                </div>
            )}
        </div>
    );
};

export default CurrencyTab;
